/**
 * Org Integrations admin page.
 *
 * Slice history:
 *   - S2: read-only shell listing existing ORG connections.
 *   - S6: added the catalog grid (filtered to `tier: ORG`) so admins
 *     can actually add a connection — closed RFC §4.1.
 *   - S8 (this revision): added the audit feed (`OrgConnectionAuditPanel`,
 *     RFC §4.9) and admin-only gating on Connect / Revoke. The audit
 *     panel is visible to every member; the Connect catalog and
 *     ConnectionList write actions only render for OWNER/ADMIN. The
 *     server still enforces via `requireOrgAdmin` — the UI gate is
 *     pure UX, never a security boundary.
 */

import React from 'react';
import {Container, Box, Typography, Paper, Alert, Divider} from '@mui/material';
import {useQuery} from '@apollo/client';
import {
  ConnectionList,
  IntegrationCatalogGrid,
  OrgConnectionAuditPanel,
} from '../components/integrations';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {useOrg} from '../contexts/OrgContext';
import {GET_ORG_CONNECTIONS} from '../graphql/queries';
import {
  ConnectionStatus,
  ConnectionTier,
  type IntegrationConnection,
} from '../types/generated/graphql';

interface OrgConnectionsQueryResult {
  orgConnections: IntegrationConnection[];
}

export default function OrgIntegrationsPage() {
  useDocumentTitle('Integrations');
  const {activeOrg, activeOrgRole, isActiveOrgAdmin} = useOrg();

  const {data: orgConnData} = useQuery<OrgConnectionsQueryResult>(
    GET_ORG_CONNECTIONS,
    {skip: !activeOrg, variables: {orgId: activeOrg?.id ?? ''}, fetchPolicy: 'cache-and-network'},
  );

  const connectedIntegrationIds = (orgConnData?.orgConnections ?? [])
    .filter(
      (c) =>
        c.status !== ConnectionStatus.Disconnected &&
        c.status !== ConnectionStatus.Revoked,
    )
    .map((c) => c.integrationId);

  return (
    <Box sx={{flex: 1, bgcolor: 'background.default'}}>
      <Container maxWidth="lg" sx={{py: 4}}>
        <Typography variant="h5" sx={{mb: 1, fontWeight: 600}}>
          Integrations
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
          Connect your organization's tools to enable automation across
          guides. These connections are shared with all members, but tokens
          are never visible to admins.
        </Typography>

        {!activeOrg ? (
          <Alert severity="info" sx={{mb: 3}}>
            Select an organization to manage its integrations.
          </Alert>
        ) : (
          <>
            {!isActiveOrgAdmin && (
              <Alert severity="info" sx={{mb: 3}}>
                You're viewing this page as a {activeOrgRole?.toLowerCase() ?? 'member'}.
                Only organization owners and admins can connect or revoke
                shared integrations. The audit feed below shows everything
                that has touched this org.
              </Alert>
            )}

            <Paper variant="outlined" sx={{p: 3, mb: 3}}>
              <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 2}}>
                Connected to {activeOrg.name}
              </Typography>
              <ConnectionList
                tierFilter={ConnectionTier.Org}
                orgId={activeOrg.id}
                readonly={!isActiveOrgAdmin}
                emptyMessage={
                  isActiveOrgAdmin
                    ? "No organization integrations connected yet. Pick one from the catalog below to get started."
                    : "No organization integrations connected yet. Ask an admin to add one."
                }
              />
            </Paper>

            {isActiveOrgAdmin && (
              <>
                <Divider sx={{my: 3}} />

                <Paper variant="outlined" sx={{p: 3, mb: 3}}>
                  <IntegrationCatalogGrid
                    tier={ConnectionTier.Org}
                    orgId={activeOrg.id}
                    connectedIntegrationIds={connectedIntegrationIds}
                    title="Available integrations"
                  />
                </Paper>
              </>
            )}

            <Divider sx={{my: 3}} />

            <Paper variant="outlined" sx={{p: 3, mb: 3}}>
              <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 0.5}}>
                Audit feed
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                Every connection that has touched this org — including
                personal connections members consented to use inside org
                runs. Token bytes are never exposed here.
              </Typography>
              <OrgConnectionAuditPanel orgId={activeOrg.id} />
            </Paper>
          </>
        )}

        <Typography variant="caption" color="text.secondary">
          Connected integrations use OAuth managed by Planapus. Tokens are
          automatically refreshed. If a connection shows an error, ask an
          admin to use the Reconnect button to re-authorize.
        </Typography>
      </Container>
    </Box>
  );
}
